import React from 'react';
import Balancer from 'react-wrap-balancer';

import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';
import { Link } from '@components/parts/Link';

import { LinkProps } from '@customTypes/Link';

type ChecklistItem = {
  title: string;
  description: string;
  actions: string;
};

type Props = {
  title: string;
  titleHint: string;
  items: ChecklistItem[];
  action?: LinkProps;
};

const Wrapper = styled.div`
  @media (min-width: 992px) {
    padding: 160px 0;
    background-color: #eff7ff;
  }
  .container {
    .content {
      background: white;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      @media (min-width: 992px) {
        padding: 50px;
        flex-direction: row;
      }
    }
  }
  .left {
    .hint {
      font-weight: 500;
      margin-bottom: 0.5em;
    }
    .title {
      font-weight: 700;
      font-size: 2.2em;
      @media (min-width: 992px) {
        padding-right: 100px;
      }
    }
    .link {
      margin-top: 40px;
      display: none;
      @media (min-width: 992px) {
        display: flex;
      }
    }
  }
  .right {
    .items {
      margin-top: 50px;
      @media (min-width: 992px) {
        margin-top: 0;
      }
      .item {
        border-left: 1px solid #e3e5ea;
        padding-left: 36px;
        position: relative;
        &:first-child {
          &::before {
            // 위에 꽁다리 선 안보이게 패치 붙이기
            position: absolute;
            top: 0px;
            left: -6px;
            display: block;
            content: '';
            width: 10px;
            height: 15px;
            background-color: white;
          }
        }
        &:last-child {
          margin-bottom: 24px;
          @media (min-width: 992px) {
            margin-bottom: 0;
          }
        }
        &:not(:last-child) {
          padding-bottom: 50px;
        }
        .title {
          position: relative;
          &::before {
            position: absolute;
            top: 10px;
            @media (min-width: 992px) {
              top: 15px;
            }
            left: -41.5px;
            display: block;
            content: '';
            width: 10px;
            height: 10px;
            background-color: #e3e5ea;
            border-radius: 5px;
          }
        }
        .description {
          margin-top: 0.7em;
          font-weight: 400;
          color: #62656a;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.8em;
          margin-top: 1.5em;
          .action {
            background: #eff7ff;
            border-radius: 4px;
            padding: 0.5em 0.7em;
            font-weight: 500;
            color: #055cf5;
            display: flex;
            align-items: center;
            font-size: 16px;
            svg {
              margin-left: 0.2em;
              width: 20px;
            }
            @media (min-width: 992px) {
              font-size: 20px;
              svg {
                width: 24px;
              }
            }
          }
        }
      }
    }
    .link {
      margin-top: 30px;
      margin-bottom: 90px;
      display: flex;
      @media (min-width: 992px) {
        display: none;
      }
    }
  }
`;

const RightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_993_9525)">
      <path d="M5 12H19" stroke="#055CF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 18L19 12" stroke="#055CF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 6L19 12" stroke="#055CF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_993_9525">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const Checklist: React.FC<Props> = (props) => {
  const { title, titleHint, items, action } = props;
  return (
    <Wrapper>
      <Container>
        <div className="content">
          <ScrollReveal className="left">
            <h5 className="hint">{titleHint}</h5>
            <h3 className="title">
              <Balancer>{title}</Balancer>
            </h3>
            {action ? <Link {...action} /> : null}
          </ScrollReveal>
          <div className="right">
            <div className="items">
              {items.map((item, idx) => (
                <ScrollReveal className="item" key={idx}>
                  <h5 className="title">{item.title}</h5>
                  <div className="description">
                    <Balancer>{item.description}</Balancer>
                  </div>
                  <div className="actions">
                    {item.actions?.length
                      ? item.actions
                          .trim()
                          .split('\n')
                          .map((action) => action.split('|'))
                          .map(([title, link]) => (
                            <a key={title} className="action" href={link}>
                              {title}
                              <RightIcon />
                            </a>
                          ))
                      : null}
                  </div>
                </ScrollReveal>
              ))}
            </div>
            {action ? <Link {...action} /> : null}
          </div>
        </div>
      </Container>
    </Wrapper>
  );
};
